// WorkbenchPanel — the repair docket (Field Notes direction A).
//
// A workshop job card, not an IDE: briefing reads as a field report
// (Newsreader italic), files are paper index tabs over a recessed
// writing well (Plex Mono), checks are a clipboard list whose hollow
// stone lights flip to quiet forest checks — that flip and the
// logged-repair card are the only two moments that move.
import React from 'react';

import useWorkbench from './useWorkbench';
import './workbench.css';

function gradeBand(grade) {
  if (grade >= 9) return 'mastered';
  if (grade >= 7) return 'proficient';
  if (grade >= 5) return 'developing';
  return 'struggling';
}

function CheckRow({ check, passed, stale, justFlipped }) {
  const light =
    passed === true ? 'wb-light pass' : passed === false ? 'wb-light fail' : 'wb-light';
  return (
    <li className={`wb-check${stale ? ' stale' : ''}`} data-testid={`check-${check.id}`}>
      <span className={`${light}${justFlipped ? ' wb-rise' : ''}`} aria-hidden>
        {passed === true ? '✓' : ''}
      </span>
      <span className="wb-check-label">
        {check.label}
        <span className="wb-check-concept">{check.concept}</span>
      </span>
    </li>
  );
}

export default function WorkbenchPanel({ courseId, domain, sessionId, onClose }) {
  const { state, edit, selectFile, runChecks, submit, abandon, flipped } =
    useWorkbench({ courseId, domain, sessionId });

  if (state.phase === 'loading') {
    return (
      <div className="wb-docket" data-testid="workbench">
        <p className="wb-kicker">repair docket</p>
        <p className="wb-savestate">putting a scenario on the bench…</p>
      </div>
    );
  }
  if (state.phase === 'error') {
    return (
      <div className="wb-docket" data-testid="workbench">
        <p className="wb-error">{state.error}</p>
      </div>
    );
  }

  const { session } = state;
  const selected = session.files.find((f) => f.path === state.selectedPath);
  const anyDirty = Object.keys(state.dirty).length > 0;
  const submitted = state.phase === 'submitted';
  const busy = state.phase === 'submitting' || state.checking;

  return (
    <div className="wb-docket" data-testid="workbench">
      <div className="wb-head">
        <div>
          <p className="wb-kicker">repair docket</p>
          <h1 className="wb-title">{session.title}</h1>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span className="wb-domain">{session.domain}</span>
          {!submitted && (
            <div>
              <button
                type="button"
                className="wb-aside-btn"
                onClick={async () => { await abandon(); onClose?.(); }}
              >
                set it aside
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="wb-body">
        <aside>
          <section className="wb-brief">
            <h3>the situation</h3>
            <p>{session.briefing}</p>
          </section>

          <section className="wb-checks" aria-label="repair checklist">
            <h3 className="wb-kicker" style={{ margin: '0 0 4px' }}>
              repair checklist
            </h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {session.checks.map((c) => (
                <CheckRow
                  key={c.id}
                  check={c}
                  passed={state.results[c.id]}
                  stale={state.lightsStale}
                  justFlipped={flipped.current.includes(c.id)}
                />
              ))}
            </ul>
            {state.lightsStale && (
              <p className="wb-stale-note">edits not yet checked — lights are stale</p>
            )}
            {!submitted && (
              <button
                type="button"
                className="wb-run-btn"
                onClick={runChecks}
                disabled={busy}
                data-testid="run-checks"
              >
                {state.checking ? 'checking…' : 'run checks'}
              </button>
            )}
          </section>
        </aside>

        <main>
          <nav className="wb-tabs" aria-label="files">
            {session.files.map((f) => (
              <button
                key={f.path}
                type="button"
                className={`wb-tab${f.path === state.selectedPath ? ' active' : ''}`}
                onClick={() => selectFile(f.path)}
              >
                {f.path}
                {f.read_only && <span className="ro">ref</span>}
                {state.dirty[f.path] && <span className="dot">•</span>}
              </button>
            ))}
          </nav>
          <div className="wb-well">
            <textarea
              className="wb-editor"
              value={state.contents[state.selectedPath] ?? ''}
              onChange={(e) => edit(state.selectedPath, e.target.value)}
              readOnly={submitted || !!selected?.read_only}
              spellCheck={false}
              aria-label={`file ${state.selectedPath}`}
              data-testid="editor"
            />
          </div>
          <div className="wb-foot">
            <span className="wb-savestate">
              {anyDirty ? 'unsaved edits' : 'all changes saved'}
            </span>
            {!submitted && (
              <button
                type="button"
                className="wb-submit"
                onClick={submit}
                disabled={busy}
                data-testid="submit-repair"
              >
                {state.phase === 'submitting' ? 'logging…' : 'log the repair'}
              </button>
            )}
          </div>

          {state.error && state.phase !== 'error' && (
            <p className="wb-error">{state.error}</p>
          )}

          {submitted && (
            <section className="wb-logged wb-rise" data-testid="repair-logged">
              <h3>{state.solved ? 'Repair logged — all checks pass.' : 'Repair logged.'}</h3>
              <p>{state.feedback}</p>
              <div className="wb-grades">
                {Object.entries(state.grades || {}).map(([concept, grade]) => (
                  <span key={concept} className={`wb-grade ${gradeBand(grade)}`}>
                    {concept} <b>{grade}/10</b>
                  </span>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
